import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText,
  Divider,
  Alert,
  Paper,
  TextField,
  Button,
  CircularProgress
} from '@mui/material';
import { 
  Person,
  CheckCircle,
  Star,
  AccountBalance,
  TrendingUp,
  Verified,
  Calculate,
  PersonAdd,
  Link
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const RulesPage = () => {
  const [xmAccountNumber, setXmAccountNumber] = useState('');
  const [verificationStatus, setVerificationStatus] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);

  const handleAccountNumberChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setXmAccountNumber(value);
    setVerificationStatus('');
  };

  const checkAffiliation = async () => {
    if (!xmAccountNumber.trim()) {
      setVerificationStatus('Please enter an account number');
      return;
    }

    setIsVerifying(true);
    setVerificationStatus('');

    try {
      const response = await fetch(
        `https://us-central1-fxwars.cloudfunctions.net/checkAffiliation?accountNumber=${encodeURIComponent(xmAccountNumber)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      const data = await response.json();

      if (response.ok) {
        if (data.isAffiliated) {
          setVerificationStatus('✅ Account successfully verified and affiliated with T1 FX Team!');
        } else {
          setVerificationStatus('❌ Account not found or not affiliated with T1 FX Team. Please check your account number or contact support.');
        }
      } else {
        setVerificationStatus('⚠️ Verification failed. Please try again or contact support.');
      }
    } catch (error) {
      console.error('Error checking affiliation:', error);
      setVerificationStatus('⚠️ Network error. Please check your connection and try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  const generalRules = [
    {
      title: "Account Requirements",
      icon: <AccountBalance color="primary" />,
      rules: [
        "Must be 18 years and older",
        "Verified trading account with XM Global or Weltrade",
        "Only Standard trading accounts are eligible",
        "Account must be in good standing",
        "All accounts must be affiliated with T1 FX Team",
        "KYC verification completed"
      ]
    },
    {
      title: "Competition Entry",
      icon: <Star color="primary" />,
      rules: [
        "Registration required before competition starts",
        "Percentage Growth participants must enter on XM Global",
        "All trades must be open for more than 3 minutes to qualify",
        "Confirmation of affiliation with T1 FX Team",
        "Agreement to all terms and conditions",
        "Valid contact information provided"
      ]
    }
  ];

  return (
    <Box sx={{ pt: 12, pb: 8, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', minHeight: '100vh' }}>
      <Container maxWidth="lg">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Box textAlign="center" mb={6}>
            <Typography variant="h2" gutterBottom sx={{ 
              background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 'bold'
            }}>
              FX Wars Competition Rules
            </Typography>
            <Typography variant="h5" color="text.secondary" mb={2}>
              Official Trading Competition Guidelines
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto' }}>
              Welcome to FX Wars! Please read all rules carefully before participating. 
              All participants must comply with these regulations to remain eligible for prizes.
            </Typography>
          </Box>
        </motion.div>

        {/* Competition Timeline */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.05 }}
        >
          <Paper sx={{ p: 4, mb: 4, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
            <Typography variant="h4" gutterBottom textAlign="center">
              🗓️ Official Competition Timeline
            </Typography>
            <Grid container spacing={3} justifyContent="center">
              <Grid item xs={12} sm={6} md={4}>
                <Box textAlign="center">
                  <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                    Competition Start
                  </Typography>
                  <Typography variant="h4" sx={{ color: '#4CAF50', fontWeight: 'bold' }}>
                    June 1, 2025
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    00:00:00 UTC+2
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Box textAlign="center">
                  <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                    Competition End
                  </Typography>
                  <Typography variant="h4" sx={{ color: '#FF5722', fontWeight: 'bold' }}>
                    December 31, 2025
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    23:59:59 UTC+2
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box textAlign="center">
                  <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                    Duration
                  </Typography>
                  <Typography variant="h4" sx={{ color: '#FFC107', fontWeight: 'bold' }}>
                    7 Months
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    214 Trading Days
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </motion.div>

        {/* Important Alert */}
        <Alert severity="warning" sx={{ mb: 4 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            📢 Important: All participants must acknowledge they have read and understood these rules. 
            Violation of any rule may result in disqualification and prize forfeiture.
          </Typography>
        </Alert>

        {/* Quick Reference */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <Paper sx={{ p: 3, mb: 6, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
            <Typography variant="h4" gutterBottom textAlign="center">
              ⚡ Quick Reference
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Box textAlign="center">
                  <Person sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h6" fontWeight="bold">Age Requirement</Typography>
                  <Typography variant="body2">18+ years old</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box textAlign="center">
                  <AccountBalance sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h6" fontWeight="bold">Platforms</Typography>
                  <Typography variant="body2">XM Global & Weltrade</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box textAlign="center">
                  <Link sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h6" fontWeight="bold">Affiliation</Typography>
                  <Typography variant="body2">T1 FX Team Required</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box textAlign="center">
                  <Verified sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h6" fontWeight="bold">Verification</Typography>
                  <Typography variant="body2">KYC Required</Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </motion.div>

        {/* Eligibility Requirements */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Box mb={6}>
            <Typography variant="h3" gutterBottom textAlign="center" mb={4}>
              📋 Eligibility Requirements
            </Typography>
            <Grid container spacing={4}>
              {generalRules.map((section, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <Card sx={{ height: '100%', '&:hover': { transform: 'translateY(-4px)', transition: 'transform 0.2s' } }}>
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={2}>
                        {section.icon}
                        <Typography variant="h6" fontWeight="bold" ml={1}>
                          {section.title}
                        </Typography>
                      </Box>
                      <List dense>
                        {section.rules.map((rule, ruleIndex) => (
                          <ListItem key={ruleIndex} sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 20 }}>
                              <CheckCircle sx={{ fontSize: 16, color: '#4CAF50' }} />
                            </ListItemIcon>
                            <ListItemText primary={rule} />
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </motion.div>

        {/* Points Calculation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <Box mb={6}>
            <Typography variant="h3" gutterBottom textAlign="center" mb={4}>
              🎯 Points Calculation System
            </Typography>
            <Card sx={{ p: 4 }}>
              <Box display="flex" alignItems="center" justifyContent="center" mb={3}>
                <Calculate sx={{ fontSize: 40, mr: 2, color: '#E91E63' }} />
                <Typography variant="h5" fontWeight="bold" color="primary">
                  XM Global Points Formula
                </Typography>
              </Box>
              <Typography variant="body1" mb={3} color="text.secondary" textAlign="center">
                Best overall trader determined by points accumulated on XM Global:
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Card sx={{ textAlign: 'center', background: 'linear-gradient(135deg, #FFD700 0%, #FFA000 100%)', color: 'white' }}>
                    <CardContent>
                      <Star sx={{ fontSize: 40, mb: 1 }} />
                      <Typography variant="h6" fontWeight="bold">Gold</Typography>
                      <Typography variant="h4" fontWeight="bold">1 Point</Typography>
                      <Typography variant="body2">per lot traded</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card sx={{ textAlign: 'center', background: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)', color: 'white' }}>
                    <CardContent>
                      <Star sx={{ fontSize: 40, mb: 1 }} />
                      <Typography variant="h6" fontWeight="bold">Bitcoin (BTC)</Typography>
                      <Typography variant="h4" fontWeight="bold">2 Points</Typography>
                      <Typography variant="body2">per lot traded</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card sx={{ textAlign: 'center', background: 'linear-gradient(135deg, #9E9E9E 0%, #757575 100%)', color: 'white' }}>
                    <CardContent>
                      <Star sx={{ fontSize: 40, mb: 1 }} />
                      <Typography variant="h6" fontWeight="bold">Silver</Typography>
                      <Typography variant="h4" fontWeight="bold">3 Points</Typography>
                      <Typography variant="body2">per lot traded</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
              
              <Box sx={{ 
                mt: 4, 
                p: 3, 
                background: 'linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 100%)',
                borderRadius: 2,
                border: '1px solid #2196F3',
                textAlign: 'center'
              }}>
                <Typography variant="h6" fontWeight="bold" color="primary">
                  Formula: Total Points = Instrument Multiplier × Total Lots Traded
                </Typography>
              </Box>
            </Card>
          </Box>
        </motion.div>

        {/* How to Affiliate */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <Box mb={6}>
            <Paper elevation={4} sx={{ p: 4, borderRadius: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
              <Box display="flex" alignItems="center" justifyContent="center" mb={4} sx={{ flexDirection: { xs: 'column', sm: 'row' }, textAlign: { xs: 'center', sm: 'left' } }}>
                <PersonAdd sx={{ fontSize: 40, mr: { xs: 0, sm: 2 }, mb: { xs: 1, sm: 0 } }} />
                <Typography variant="h3" fontWeight="bold" sx={{ fontSize: { xs: '1.8rem', sm: '2.5rem', md: '3rem' } }}>
                  How to Affiliate with T1 FX Team
                </Typography>
              </Box>
              
              <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                  <Card sx={{ 
                    background: 'rgba(255,255,255,0.15)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    color: 'white',
                    height: '100%'
                  }}>
                    <CardContent sx={{ p: 4 }}>
                      <Box display="flex" alignItems="center" mb={3}>
                        <img 
                          src="/partnersLogo-15years.svg" 
                          alt="XM Global Logo" 
                          style={{ 
                            width: '50px', 
                            height: 'auto', 
                            marginRight: '15px'
                          }} 
                        />
                        <Typography variant="h5" fontWeight="bold">
                          XM Global Affiliation
                        </Typography>
                      </Box>
                      <List>
                        <ListItem sx={{ px: 0 }}>
                          <ListItemIcon>
                            <Typography variant="h6" fontWeight="bold" color="#FFD700">1</Typography>
                          </ListItemIcon>
                          <ListItemText 
                            primary="Create XM Global Account"
                            secondary={
                              <Box>
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                  Visit XM Global website and register for a new trading account:
                                </Typography>
                                <Button
                                  href="https://affs.click/bwvBB"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  variant="contained"
                                  size="small"
                                  sx={{
                                    mt: 1,
                                    backgroundColor: '#FFD700',
                                    color: '#000000',
                                    fontWeight: 'bold',
                                    '&:hover': {
                                      backgroundColor: '#FFC107',
                                    }
                                  }}
                                >
                                  Open XM Global Account
                                </Button>
                              </Box>
                            }
                            primaryTypographyProps={{ color: 'white', fontWeight: 'bold' }}
                          />
                        </ListItem>
                        <ListItem sx={{ px: 0 }}>
                          <ListItemIcon>
                            <Typography variant="h6" fontWeight="bold" color="#FFD700">2</Typography>
                          </ListItemIcon>
                          <ListItemText 
                            primary="Use T1 FX Team Referral Code"
                            secondary={
                              <Box>
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                  Enter our exclusive referral code during registration process:
                                </Typography>
                                <Box sx={{ 
                                  mt: 1, 
                                  p: 1, 
                                  backgroundColor: 'rgba(255,255,255,0.15)',
                                  borderRadius: 1,
                                  border: '1px solid #FFD700'
                                }}>
                                  <Typography variant="h6" sx={{ color: '#FFD700', fontWeight: 'bold', textAlign: 'center' }}>
                                    T1FXTEAM
                                  </Typography>
                                </Box>
                              </Box>
                            }
                            primaryTypographyProps={{ color: 'white', fontWeight: 'bold' }}
                          />
                        </ListItem>
                        <ListItem sx={{ px: 0 }}>
                          <ListItemIcon>
                            <Typography variant="h6" fontWeight="bold" color="#FFD700">3</Typography>
                          </ListItemIcon>
                          <ListItemText 
                            primary="Verify Your Account"
                            secondary="Complete KYC verification and make initial deposit"
                            primaryTypographyProps={{ color: 'white', fontWeight: 'bold' }}
                            secondaryTypographyProps={{ color: 'rgba(255,255,255,0.8)' }}
                          />
                        </ListItem>
                        <ListItem sx={{ px: 0 }}>
                          <ListItemIcon>
                            <Typography variant="h6" fontWeight="bold" color="#FFD700">4</Typography>
                          </ListItemIcon>
                          <ListItemText 
                            primary="Verify Your Affiliation"
                            secondary={
                              <Box>
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                  Check if your account is properly affiliated with T1 FX Team:
                                </Typography>
                                <TextField
                                  fullWidth
                                  value={xmAccountNumber}
                                  onChange={handleAccountNumberChange}
                                  variant="outlined"
                                  size="small"
                                  sx={{ 
                                    mt: 2,
                                    mb: 2,
                                    '& .MuiOutlinedInput-root': {
                                      backgroundColor: 'rgba(255,255,255,0.95)',
                                      color: '#000000',
                                      '& fieldset': {
                                        borderColor: 'rgba(255,255,255,0.5)',
                                      },
                                      '&:hover fieldset': {
                                        borderColor: '#FFD700',
                                      },
                                      '&.Mui-focused fieldset': {
                                        borderColor: '#FFD700',
                                        borderWidth: '2px'
                                      },
                                    }
                                  }}
                                  placeholder="Enter your XM Global account number"
                                />
                                <Button
                                  onClick={checkAffiliation}
                                  disabled={isVerifying || !xmAccountNumber.trim()}
                                  variant="contained"
                                  sx={{
                                    backgroundColor: '#FFD700',
                                    color: '#000000',
                                    fontWeight: 'bold',
                                    '&:hover': {
                                      backgroundColor: '#FFC107',
                                    },
                                    '&:disabled': {
                                      backgroundColor: 'rgba(255,255,255,0.3)',
                                      color: 'rgba(255,255,255,0.5)',
                                    }
                                  }}
                                  startIcon={isVerifying ? <CircularProgress size={20} color="inherit" /> : <Verified />}
                                >
                                  {isVerifying ? 'Verifying...' : 'Verify Affiliation'}
                                </Button>
                                {verificationStatus && (
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      mt: 2, 
                                      p: 2, 
                                      borderRadius: 1,
                                      backgroundColor: verificationStatus.includes('✅') ? 'rgba(76, 175, 80, 0.2)' : 
                                                     verificationStatus.includes('❌') ? 'rgba(244, 67, 54, 0.2)' : 
                                                     'rgba(255, 152, 0, 0.2)',
                                      border: `1px solid ${verificationStatus.includes('✅') ? '#4CAF50' : 
                                                          verificationStatus.includes('❌') ? '#f44336' : 
                                                          '#ff9800'}`,
                                      color: 'white',
                                      fontWeight: 'bold'
                                    }}
                                  >
                                    {verificationStatus}
                                  </Typography>
                                )}
                              </Box>
                            }
                            primaryTypographyProps={{ color: 'white', fontWeight: 'bold' }}
                          />
                        </ListItem>
                        <ListItem sx={{ px: 0 }}>
                          <ListItemIcon>
                            <Typography variant="h6" fontWeight="bold" color="#FFD700">5</Typography>
                          </ListItemIcon>
                          <ListItemText 
                            primary="Register for FX Wars Competition"
                            secondary="After verification, register on the FX Wars website to officially join the competition"
                            primaryTypographyProps={{ color: 'white', fontWeight: 'bold' }}
                            secondaryTypographyProps={{ color: 'rgba(255,255,255,0.8)' }}
                          />
                        </ListItem>
                      </List>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Card sx={{ 
                    background: 'rgba(255,255,255,0.15)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    color: 'white',
                    height: '100%'
                  }}>
                    <CardContent sx={{ p: 4 }}>
                      <Box display="flex" alignItems="center" mb={3}>
                        <TrendingUp sx={{ fontSize: 40, mr: 2, color: '#64B5F6' }} />
                        <Typography variant="h5" fontWeight="bold">
                          Weltrade Affiliation
                        </Typography>
                      </Box>
                      <List>
                        <ListItem sx={{ px: 0 }}>
                          <ListItemIcon>
                            <Typography variant="h6" fontWeight="bold" color="#64B5F6">1</Typography>
                          </ListItemIcon>
                          <ListItemText 
                            primary="Register with Weltrade"
                            secondary={
                              <Box>
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                  Sign up for a new Weltrade account through our partnership link:
                                </Typography>
                                <Button
                                  href="https://track.gowt.me/visit/?bta=40955&brand=weltrade"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  variant="contained"
                                  size="small"
                                  sx={{
                                    mt: 1,
                                    backgroundColor: '#64B5F6',
                                    color: '#000000',
                                    fontWeight: 'bold',
                                    '&:hover': {
                                      backgroundColor: '#42A5F5',
                                    }
                                  }}
                                >
                                  Open Weltrade Account
                                </Button>
                              </Box>
                            }
                            primaryTypographyProps={{ color: 'white', fontWeight: 'bold' }}
                          />
                        </ListItem>
                        <ListItem sx={{ px: 0 }}>
                          <ListItemIcon>
                            <Typography variant="h6" fontWeight="bold" color="#64B5F6">2</Typography>
                          </ListItemIcon>
                          <ListItemText 
                            primary="Apply T1 FX Team Code"
                            secondary={
                              <Box>
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                  Use our unique affiliate code when setting up your account:
                                </Typography>
                                <Box sx={{ 
                                  mt: 1, 
                                  p: 1, 
                                  backgroundColor: 'rgba(255,255,255,0.15)',
                                  borderRadius: 1,
                                  border: '1px solid #64B5F6'
                                }}>
                                  <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center' }}>
                                    CX40955
                                  </Typography>
                                </Box>
                              </Box>
                            }
                            primaryTypographyProps={{ color: 'white', fontWeight: 'bold' }}
                          />
                        </ListItem>
                        <ListItem sx={{ px: 0 }}>
                          <ListItemIcon>
                            <Typography variant="h6" fontWeight="bold" color="#64B5F6">3</Typography>
                          </ListItemIcon>
                          <ListItemText 
                            primary="Complete Account Setup"
                            secondary="Finish identity verification and fund your account"
                            primaryTypographyProps={{ color: 'white', fontWeight: 'bold' }}
                            secondaryTypographyProps={{ color: 'rgba(255,255,255,0.8)' }}
                          />
                        </ListItem>
                        <ListItem sx={{ px: 0 }}>
                          <ListItemIcon>
                            <Typography variant="h6" fontWeight="bold" color="#64B5F6">4</Typography>
                          </ListItemIcon>
                          <ListItemText 
                            primary="Register for FX Wars Competition"
                            secondary="Register on the FX Wars website to officially join the competition"
                            primaryTypographyProps={{ color: 'white', fontWeight: 'bold' }}
                            secondaryTypographyProps={{ color: 'rgba(255,255,255,0.8)' }}
                          />
                        </ListItem>
                      </List>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
              
              <Box sx={{ 
                mt: 4, 
                p: 3, 
                background: 'rgba(255,255,255,0.1)',
                borderRadius: 2,
                textAlign: 'center',
                border: '1px solid rgba(255,255,255,0.2)'
              }}>
                <Typography variant="h6" fontWeight="bold" mb={2}>
                  🚀 Ready to Join the Competition?
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  Contact T1 FX Team support for personalized assistance with account setup and affiliation. 
                  Our team will guide you through the entire process to ensure you're ready to compete!
                </Typography>
              </Box>
            </Paper>
          </Box>
        </motion.div>

        {/* Final Note */}
        <Alert severity="info">
          <Typography variant="body1">
            <strong>Rule Updates:</strong> These rules may be updated before or during the competition. 
            All participants will be notified of any changes via email and official communication channels. 
            The most current version will always be available on this page.
          </Typography>
        </Alert>
      </Container>
    </Box>
  );
};

export default RulesPage; 